import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { GLItemsDTO } from '../../../DTO/GLItemsDTO'
import { GLItemDTO, GLItemKind, GLItemStatus, GLItemStatuses } from '../../../DTO/GLItemDTO'
import { DisplayDateFromString } from '../../../services/DateTime'
import { prittifyStringFromServer } from '../../../services/TextConverter'
import mainservice from '../../../services/MainService'

const PAGE_SIZE = 10

function statusName(kind: GLItemKind, status: number): string {
  const statuses = (GLItemStatuses[kind] || []) as Array<{ status?: GLItemStatus; name: string }>
  const entry = statuses.find(s => s.status === status)
  return entry?.name || `${status}`
}

export default function NewsworthyModule() {
  const [glitems] = useState(new GLItemsDTO({
    page: 1,
    limit: PAGE_SIZE,
    kind: GLItemKind.marketing,
    newsworthy: true,
  }))
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<GLItemDTO[]>([])
  const [total, setTotal] = useState(0)

  async function load(p: number) {
    setLoading(true)
    await glitems.getPage(p)
    setItems([...glitems.items])
    setTotal(glitems.total)
    setPage(p)
    setLoading(false)
  }

  useEffect(() => {
    load(1)
  }, [])

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  function jumpToThread(item: GLItemDTO) {
    const isChild = !!(item.parentId && item.parentId > 0)
    mainservice.navTo([
      ['view', 'spMarketing'],
      ['status', isChild ? 'all' : String(item.status)],
      ['id', item.id],
    ])
  }

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        padding: 2,
        borderRadius: 1,
        border: '1px solid #ccc',
      }}
    >
      <Typography variant="h5" component="h3" sx={{ marginBottom: 2 }}>
        Newsworthy
      </Typography>

      {loading && <Box>Lade...</Box>}
      {!loading && items.length === 0 && <Box>Keine Newsworthy-Threads</Box>}

      <Stack spacing={2}>
        {!loading && items.map(item => (
          <Box
            key={item.id}
            sx={{
              border: '1px solid #ddd',
              borderRadius: 1,
              padding: 2,
            }}
          >
            <Stack spacing={1}>
              {item.parentId && item.parentId > -1 && (
                <Typography variant="body2" color="text.secondary">
                  Parent: {item.parentHeadline || `(#${item.parentId})`}
                </Typography>
              )}
              <Typography variant="h6" component="h4">
                {item.headline || '(ohne Titel)'}
              </Typography>
              <Box
                className="newsworthy-content"
                sx={{ '& p': { margin: '0 0 0.5em' } }}
                dangerouslySetInnerHTML={{ __html: prittifyStringFromServer(item.content || '') }}
              />
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                sx={{ flexWrap: 'wrap', fontSize: '0.9rem', color: '#555' }}
              >
                <span>Status: {statusName(item.kind, item.status)}</span>
                {item.person && (
                  <span>
                    Person: {item.person.getDisplayName?.() || `${item.person.firstname || ''} ${item.person.lastname || ''}`.trim()}
                  </span>
                )}
                {item.person?.customerName && (
                  <span>Firma: {item.person.customerName}</span>
                )}
                {item.target && <span>Ziel: {item.target}</span>}
                {item.nextstep && <span>Nächster Schritt: {item.nextstep}</span>}
                {item.revenue_potential != null && (
                  <span>Potenzial: {item.revenue_potential} €</span>
                )}
                {item.revenue_probability != null && (
                  <span>Wahrscheinlichkeit: {item.revenue_probability} %</span>
                )}
                {item.weighted_revenue != null && (
                  <span>Gewichtet: {item.weighted_revenue} €</span>
                )}
                {(item.manualdate || item.created) && (
                  <span>
                    Datum: {DisplayDateFromString(item.manualdate || item.created || '')}
                  </span>
                )}
              </Stack>
              <Box>
                <Button
                  variant="contained"
                  onClick={() => jumpToThread(item)}
                >
                  Zum Thread
                </Button>
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>

      {total > PAGE_SIZE && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ marginTop: 2, alignItems: 'center' }}
        >
          <Button
            disabled={page <= 1 || loading}
            onClick={() => load(page - 1)}
          >
            Zurück
          </Button>
          <Typography>
            Seite {page} / {pages} ({total} Einträge)
          </Typography>
          <Button
            disabled={page >= pages || loading}
            onClick={() => load(page + 1)}
          >
            Weiter
          </Button>
        </Stack>
      )}
    </Box>
  )
}
